import { GiftSettlementStatus } from '@shared/api/giftSupplySettlement/giftSupplySettlement.interface';
import { SettlementInfo } from '@shared/api/giftItemBrand/giftItemBrand.interface';

// ─────────────────────────────────────────────
// 상수
// ─────────────────────────────────────────────

/** 브랜드 정보가 없는 배송건을 구분하는 sentinel 값 */
export const NO_BRAND = '__none__';

/** 정산 완료 내역 캘린더용 1~12월 배열 */
export const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);

// ─────────────────────────────────────────────
// 타입
// ─────────────────────────────────────────────

/** 테이블 컬럼 정렬 방향 */
export type SortDirection = 'asc' | 'desc';

/** 정산 완료 내역 정렬 키 */
export type HistorySortKey = 'month' | 'amount';

/** 정산 관리 페이지 탭 */
export type SettlementTab = 'delivered' | 'pendingAndHistory';

/** 정산 관리 페이지 탭 목록 */
export const SETTLEMENT_TABS: { key: SettlementTab; label: string }[] = [
  { key: 'pendingAndHistory', label: '정산 예정 및 완료 내역' },
  { key: 'delivered', label: '배송 완료 내역' },
];

// ─────────────────────────────────────────────
// 날짜 유틸
// ─────────────────────────────────────────────

/**
 * Date 객체를 `YYYY-MM-DD` 형식의 문자열로 변환한다.
 * date input의 `value` 속성에 직접 사용할 수 있다.
 */
export const toYmd = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/**
 * 한국 시간(KST, Asia/Seoul) 기준으로 오늘의 연·월·일을 반환한다.
 * `Date` 객체의 로컬 시간은 브라우저 시스템 설정에 따라 달라지므로
 * `Intl.DateTimeFormat`을 통해 KST 기준으로 명시적으로 계산한다.
 */
export const getKstDateParts = (): { year: number; month: number; day: number } => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }).formatToParts(new Date());
  const findPart = (type: string) =>
    Number(parts.find((p) => p.type === type)?.value ?? 0);
  return { year: findPart('year'), month: findPart('month'), day: findPart('day') };
};

// ─────────────────────────────────────────────
// 상태 표시 헬퍼
// ─────────────────────────────────────────────

/**
 * `GiftSettlementStatus` 값을 한국어 레이블로 변환한다.
 * @example statusLabel(GiftSettlementStatus.Paid) // '지급완료'
 */
export const statusLabel = (status: GiftSettlementStatus): string =>
  status === GiftSettlementStatus.Paid ? '지급완료' : '정산예정';

/**
 * `GiftSettlementStatus` 값에 대응하는 Tailwind 텍스트/배경 클래스를 반환한다.
 * `className`에 직접 스프레드하여 사용한다.
 */
export const statusColor = (status: GiftSettlementStatus): string =>
  status === GiftSettlementStatus.Paid
    ? 'text-green-700 bg-green-50'
    : 'text-amber-700 bg-amber-50';

// ─────────────────────────────────────────────
// 정산 계산 유틸
// ─────────────────────────────────────────────

/**
 * 정산완료 라인의 `surcharge`(가산액)를 소량 추가단가와 택배비로 분해한다.
 *
 * 백엔드가 surcharge를 단일 값으로 내려주기 때문에, 현재 정산정보(`SettlementInfo`)를
 * 기준으로 소량 추가단가를 역산하고 나머지를 택배비로 추정한다.
 * 정산정보가 변경된 경우 실제 집행 당시 값과 다를 수 있으므로 **추정치**임에 유의한다.
 *
 * @param quantity - 해당 라인의 배송 수량
 * @param surcharge - 백엔드에서 내려온 가산액 합계
 * @param si - 현재 브랜드의 `SettlementInfo` (없으면 전액을 택배비로 간주)
 * @returns `{ extraUnitAmount: 소량추가단가 합계, courierFee: 택배비 }`
 */
export const splitLineSurcharge = (
  quantity: number,
  surcharge: number,
  si: SettlementInfo | undefined,
): { extraUnitAmount: number; courierFee: number } => {
  if (!si) return { extraUnitAmount: 0, courierFee: surcharge };
  const freeShippingMinQuantity = si.freeShippingMinQuantity ?? 0;
  const shippingFee = si.shippingFee ?? 0;
  const extraApplies = freeShippingMinQuantity > 0 && quantity < freeShippingMinQuantity;
  const extraUnitAmount = extraApplies ? shippingFee * quantity : 0;
  return { extraUnitAmount, courierFee: surcharge - extraUnitAmount };
};
